import {
  API_URL,
  CUSTOMER_API_URL,
  SHOPIFY_PRODUCT_VARIANTS,
  SHOPIFY_UPSELL_VARIANTS
} from '../../../config';
import {
  buildCartLineItemProperties,
  findProductVariant,
  ItemProperties,
  ItemSlot,
  Product
} from '../../../types';
import { CartItemState } from '../../app/Configurator';

import { CartLineItemProperties } from '../../item/types';
import { generateLegacyProductCode } from '../../productCode';
import {
  ProductClassifications,
  SelectionVdpCodes
} from '../../productCode/types';
import { ChosenUpsell, UpsellProduct } from '../../upsells/types';

import { ApiCartItem } from '../types';

export function getChosenUpsell(
  chosenUpsellItems: ChosenUpsell[],
  upsellCartItem: {
    id: string | undefined;
    quantity: number;
    properties: CartLineItemProperties;
  }
) {
  return chosenUpsellItems.find(chosen => {
    return chosen.upsell.product_id === upsellCartItem.properties._product_id;
  });
}

export function getUpsellProductCode(
  chosenUpsell: ChosenUpsell | undefined,
  properties: ItemProperties
) {
  const upsellVdpCodes = getUpsellProductVdpCodes(chosenUpsell, properties);

  const upsellProductCode = generateLegacyProductCode(
    upsellVdpCodes,
    ProductClassifications.UPSELL
  );
  return upsellProductCode;
}

export function getProductCode(
  apiProduct: Product | undefined,
  properties: ItemProperties
) {
  const isPack = (apiProduct?.label_slots.length || 0) > 1;
  const classification = isPack
    ? ProductClassifications.PACK
    : ProductClassifications.NON_PACK;

  const vdpCodes: SelectionVdpCodes = getProductVdpCodes(
    apiProduct,
    properties
  );

  const productCode = generateLegacyProductCode(vdpCodes, classification);
  return productCode;
}

export function getUpsellProductVdpCodes(
  upsell: ChosenUpsell | undefined,
  properties: ItemProperties
): SelectionVdpCodes {
  const upsellProduct = upsell?.upsell;

  const [labelSlot] = upsellProduct?.label_slots ?? [null];
  const [slotOption] = labelSlot?.slot_options ?? [null];
  const label =
    upsellProduct?.labels.find(
      ({ label_id }) => label_id === slotOption?.label_id
    ) ?? null;

  return {
    product: upsellProduct?.vdpcode ?? null,
    label: label?.vdpcode ?? null,
    style: properties.style?.vdpcode ?? null,
    palette: properties.palette?.vdpcode ?? null,
    icon: properties.icon?.vdpcode ?? null,
    font: properties.font?.vdpcode ?? null,
    labelVariant: null,
    material: upsell?.material?.vdpcode ?? null,
    shoe: null
  };
}

export function getProductVdpCodes(
  apiProduct: Product | undefined,
  properties: ItemProperties
): SelectionVdpCodes {
  return {
    product: apiProduct?.vdpcode ?? null,
    label: getLabel(properties, apiProduct)?.vdpcode ?? null,
    style: properties.style?.vdpcode ?? null,
    palette: properties.palette?.vdpcode ?? null,
    icon: properties.icon?.vdpcode ?? null,
    font: properties.font?.vdpcode ?? null,
    labelVariant: getPackShape(properties, apiProduct)?.vdpcode ?? null,
    material: properties.clothingType?.vdpcode ?? null,
    shoe: getShoe(properties, apiProduct)?.vdpcode ?? null
  };
}

export function getShoe(
  properties: ItemProperties,
  apiProduct: Product | undefined
) {
  let shoe = null;
  for (const slotId in Object.keys(properties.slots)) {
    const slot = properties.slots[slotId];

    if (!slot) continue;

    const label = apiProduct?.labels.find(
      ({ label_id }) => label_id === slot.labelId
    );

    if (!label) continue;

    const lowerSystemName = label?.system_name?.toLowerCase();
    const isShoeLabel = lowerSystemName?.includes('shoe');
    if (isShoeLabel) {
      shoe =
        label?.variants.find(variant => {
          return variant.label_variant_id === slot.variantId;
        }) ?? null;
      break;
    }
  }
  return shoe;
}

export function getLabel(
  properties: ItemProperties,
  apiProduct: Product | undefined
) {
  const firstLabelId = Object.values(properties.slots)[0]?.labelId;
  const label = apiProduct?.labels.find(
    ({ label_id }) => label_id === firstLabelId
  );
  return label;
}

export function getPackShape(
  properties: ItemProperties,
  apiProduct: Product | undefined
) {
  let labelVariant = null;
  const slotIds = Object.keys(properties.slots);
  for (const slotId of slotIds) {
    const slot = properties.slots[slotId];

    if (!slot) continue;

    const label = apiProduct?.labels.find(
      ({ label_id }) => label_id === slot?.labelId
    );

    if (!label) continue;

    // if system name contains shoe
    // cannot be "pack shape"
    const lowerSystemName = label?.system_name?.toLowerCase();
    const isShoeLabel = lowerSystemName?.includes('shoe');
    if (isShoeLabel) continue;

    const styleId = properties?.style?.style_id;

    // if does not have more than one variant supporting chosen style
    // cannot be "pack shape"
    const validVariants = label?.variants.filter(
      variant => variant.style_id === styleId
    );
    const hasMultipleVariants = (validVariants?.length || 0) < 2;
    if (hasMultipleVariants) continue;

    // if is iterative
    // cannot be "pack shape"
    const isIterative =
      styleId && label?.iterative_style_ids?.includes(styleId);
    if (isIterative) continue;

    labelVariant = label?.variants.find(variant => {
      return variant.label_variant_id === slot.variantId;
    });
  }
  return labelVariant;
}

export function buildPrimaryApiCartItem(
  properties: ItemProperties,
  apiProduct: Product | undefined,
  // The live preview <svg> (see the DOM read below) only ever reflects
  // whichever configuration is currently active/rendered - for a
  // multi-config session, each configuration's own preview must be captured
  // by switching to it first, so the caller passes that capture in here
  // rather than relying on this function's own (necessarily "whatever's on
  // screen right now") DOM read.
  previewSvgOverride?: string
) {
  const apiCartItemTextFields = Object.values(properties.fields).map(
    ({ field_id, value }) => {
      return { text_field_id: field_id, value };
    }
  );

  const apiCartItemLabelSlots = Object.entries(properties.slots).map(
    ([slotId, { labelId, variantId, materialId }]) => {
      return {
        slot_id: slotId,
        label_id: labelId,
        label_variant_id: variantId,
        material_id: materialId
      };
    }
  );

  const apiCartItemData = {
    product_id: apiProduct?.product_id,
    style_id: properties.style?.style_id || null,
    palette_id: properties.palette?.palette_id || null,
    font_id: properties.font?.font_id || null,
    icon_id: properties.icon?.icon_id || null,
    text_fields: apiCartItemTextFields,
    label_slots: apiCartItemLabelSlots,
    legacy_product_code: getProductCode(apiProduct, properties),
    previewSvg:
      previewSvgOverride ??
      document.getElementById(`product-preview-${apiProduct?.product_id}`)
        ?.outerHTML ??
      ''
  };

  return apiCartItemData;
}

export interface BuildCartItemPropertiesOptions {
  apiProduct: Product | undefined;
  cartItemId: string;
  fonts: unknown[] | undefined;
  showIcons: boolean;
  showLabelShapes: boolean;
  materials: unknown[] | undefined;
  hideFontsAndFields: boolean;
  labelHasMultipleVariants: Record<string, boolean>;
}

// Shared by the single-configuration add-to-cart flow and the multi-config
// flow (one call per configuration) - arranges the Shopify cart line
// `properties` object (display fields + underscore-prefixed metadata).
export function buildCartItemProperties(
  properties: ItemProperties,
  {
    apiProduct,
    cartItemId,
    fonts,
    showIcons,
    showLabelShapes,
    materials,
    hideFontsAndFields,
    labelHasMultipleVariants
  }: BuildCartItemPropertiesOptions
): Record<string, string | undefined> {
  const primaryCartItemProperties = buildCartLineItemProperties(properties);

  const {
    Font,
    ['Clothing Type']: clothingType,
    Icon,
    Style,
    Palette,
    ...otherPrimaryCartItemProperties
  } = primaryCartItemProperties;

  const fieldNames: { [key: string]: string | undefined } = {};

  const hasNameField = otherPrimaryCartItemProperties.Name;
  if (hasNameField) {
    fieldNames.Name = otherPrimaryCartItemProperties.Name;
    delete otherPrimaryCartItemProperties.Name;
  }

  const hasFullNameField = otherPrimaryCartItemProperties['Full Name'];
  if (hasFullNameField) {
    fieldNames['Full Name'] = otherPrimaryCartItemProperties['Full Name'];
    delete otherPrimaryCartItemProperties['Full Name'];
  }

  if (!hideFontsAndFields) {
    Object.entries(otherPrimaryCartItemProperties).forEach(([key, value]) => {
      if (!key.startsWith('_')) {
        fieldNames[key] = value;
        delete otherPrimaryCartItemProperties[key];
      }
    });
  }

  const userProperties: { [key: string]: string | undefined } = {
    Style,
    Color: Palette
  };

  if ((fonts?.length ?? 0) > 1) {
    userProperties.Font = Font;
  }

  if (showIcons) {
    userProperties.Icon = Icon;
  }

  const shapes: string[] = [];
  if (showLabelShapes && properties.labelShape?.display_name) {
    shapes.push(properties.labelShape.display_name);
  }
  apiProduct?.label_slots?.forEach(labelSlot => {
    const labelId =
      properties.slots?.[labelSlot.product_label_slot_id]?.labelId;
    const label = apiProduct.labels.find(l => l.label_id === labelId);
    const isIterative = label?.iterative_style_ids?.includes(
      properties?.style?.style_id ?? ''
    );
    const hasMultipleVariants = labelHasMultipleVariants[labelId || ''];

    if (hasMultipleVariants && !isIterative) {
      const variantId =
        properties.slots[labelSlot.product_label_slot_id].variantId;
      const variant = label?.variants.find(
        v => v.label_variant_id === variantId
      );
      if (variant?.system_name) shapes.push(variant.system_name);
    }
  });

  if (shapes.length) {
    userProperties.Shape = shapes.join(', ');
  }

  if ((materials?.length ?? 0) > 1) {
    userProperties['Clothing Type'] = clothingType;
  }

  const quantity = getQuantityProperty(properties.slots, apiProduct);

  return {
    Labels: quantity.toString(),
    ...fieldNames,
    ...userProperties,
    ...otherPrimaryCartItemProperties,
    _order_system: 'NEW',
    _cart_item_id: cartItemId,
    _product_id: apiProduct?.product_id,
    _style_id: properties.style?.style_id,
    _palette_id: properties.palette?.palette_id,
    _font_id: properties.font?.font_id,
    _icon_id: properties.icon?.icon_id,
    _material_id: properties.clothingType?.material_id
  };
}

// Creates the durable multi-config session correlation record (Task 1.0's
// customer-api feature). Called once per "Add to Cart" for a multi-config
// session, before any cart items are created.
export async function createMultiConfigSession(
  productId: string
): Promise<string> {
  const response = await fetch(`${API_URL}/multi-config-sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ product_id: productId })
  });

  if (!response.ok) {
    throw new Error('Error creating multi-config session');
  }

  const { multi_config_session_id } = await response.json();
  return multi_config_session_id;
}

// Resolves the Multi-Config addon product's real Shopify variant by SKU,
// the same way processUpsells resolves upsell variants - reusing the
// Shopify theme's injected "upsells" collection data, since that's the only
// client-side source of real variant ids/prices/availability today (see
// [[project_multi_config_addon_linking]] memory for why this mirrors the
// upsell pattern rather than a tag-based lookup).
export function findMultiConfigAddonVariant(sku: string | null | undefined) {
  if (!sku) return undefined;
  // Falls back to the base product's own variants (SHOPIFY_PRODUCT_VARIANTS)
  // if the SKU isn't in the upsells collection data - guards against the
  // SKU being set up as a variant of the base product instead, or simply
  // forgotten from the "upsells" collection.
  return (
    SHOPIFY_UPSELL_VARIANTS.find(variant => variant.sku === sku) ??
    SHOPIFY_PRODUCT_VARIANTS.find(variant => variant.sku === sku)
  );
}

// Resolves the Multi-Config "included" tier's real Shopify variant by SKU -
// the configurations between position 0 (full price) and the product's
// pricing threshold (see resolveConfigVariant). Same mechanism (and same
// upsells-collection-first, base-product-variants-fallback lookup) as
// findMultiConfigAddonVariant, just a second SKU field.
export function findMultiConfigIncludedVariant(sku: string | null | undefined) {
  if (!sku) return undefined;
  return (
    SHOPIFY_UPSELL_VARIANTS.find(variant => variant.sku === sku) ??
    SHOPIFY_PRODUCT_VARIANTS.find(variant => variant.sku === sku)
  );
}

// Encodes the 3-tier multi-config pricing rule shared by
// useSendMultiConfigSessionToCart and useSaveMultiConfigSessionEdit:
// configuration #1 (index 0) is always full price; configurations after that
// but before the product's "extra pricing starts at" threshold use the
// included-tier variant; everything at or past the threshold uses the addon
// variant. Positional and recomputed on every save, matching how pricing has
// always worked here - a configuration's index, not its identity, decides
// its price.
export function resolveConfigVariant(
  index: number,
  properties: Pick<ItemProperties, 'labelShape' | 'palette' | 'style'>,
  apiProduct: Pick<Product, 'multi_config_pricing_threshold'>,
  addonVariant: ReturnType<typeof findMultiConfigAddonVariant>,
  includedVariant: ReturnType<typeof findMultiConfigIncludedVariant>
) {
  if (index === 0) {
    const { labelShape, palette, style } = properties;
    return findProductVariant(labelShape, palette, style)?.id;
  }

  const pricingThreshold = apiProduct.multi_config_pricing_threshold ?? 2;
  return index + 1 < pricingThreshold ? includedVariant?.id : addonVariant?.id;
}

export async function getApiCartItem(
  cartItemId: string
): Promise<CartItemState> {
  const res = await fetch(CUSTOMER_API_URL + `/cart-items/${cartItemId}`);
  return await res.json();
}

export interface MultiConfigSessionMembers {
  multi_config_session_id: string;
  product_id: string;
  cart_item_ids: string[];
  order_ids: string[];
}

// Resolves a multi-config session's member cart_item_ids (Task 1.0's
// GET /multi-config-sessions/:id) - used when editing an existing session
// to find every sibling of the cart item the shopper clicked "edit" on.
export async function resolveMultiConfigSession(
  sessionId: string
): Promise<MultiConfigSessionMembers> {
  const response = await fetch(`${API_URL}/multi-config-sessions/${sessionId}`);

  if (!response.ok) {
    throw new Error('Error resolving multi-config session');
  }

  return await response.json();
}

export interface ShopifyCartLine {
  key: string;
  id: number;
  quantity: number;
  properties: Record<string, string>;
}

// Reads the shopper's live Shopify cart. Used when editing an existing
// multi-config session: customer-api never stores a cart line's Shopify
// `key` or its `_multi_config_index` (both are Shopify-cart-only
// properties, stamped once at original add-to-cart time), so both have to
// be read back from the live cart rather than a customer-api record.
export async function getShopifyCart(): Promise<{ items: ShopifyCartLine[] }> {
  const response = await fetch('/cart.js');
  return await response.json();
}

export async function updateApiCartItem(
  cartItemId: string,
  apiCartItem: ApiCartItem
) {
  const cartItemResponse = await fetch(`${API_URL}/cart-items/${cartItemId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(apiCartItem)
  });

  if (!cartItemResponse.ok) {
    console.error('error saving cart item configuration', apiCartItem);
    throw new Error('Error saving item configuration');
  }
  return cartItemId;
}

export async function deleteApiCartItem(cartItemId: string) {
  const cartItemResponse = await fetch(`${API_URL}/cart-items/${cartItemId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' }
  });

  if (!cartItemResponse.ok) {
    console.error('Error deleting cart item', cartItemId);
    throw new Error('Error deleting cart item');
  }
  return cartItemId;
}

export async function sendApiCartItem(apiCartItem: ApiCartItem) {
  const cartItemResponse = await fetch(`${API_URL}/cart-items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(apiCartItem)
  });

  if (!cartItemResponse.ok) {
    console.error('error saving cart item configuration', apiCartItem);
    throw new Error('Error saving item configuration');
  }

  const { cart_item_id } = await cartItemResponse.json();
  return cart_item_id;
}

export function buildUpsellApiCartItem(
  chosenUpsell: ChosenUpsell | undefined,
  properties: ItemProperties,
  primaryApiCartItem: ApiCartItem,
  previewSvgOverride?: string
) {
  const upsellApiCartItemLabelSlots =
    chosenUpsell?.upsell.label_slots.map(slot => {
      const { product_label_slot_id: slot_id } = slot;

      // assume there is only one slot option
      const [slotOption] = slot.slot_options;
      const { label_id } = slotOption;

      // get the first variant that supports the style
      const label = chosenUpsell.upsell.labels.find(
        label => label.label_id === label_id
      );
      const variant = label?.variants.find(
        variant => variant.style_id === properties.style?.style_id
      );

      // if the slot supports the material, use it, otherwise use the first material
      const slotSupportsMaterial = slotOption.material_ids.includes(
        chosenUpsell.material?.material_id
      );
      const material_id = slotSupportsMaterial
        ? chosenUpsell.material?.material_id
        : slotOption.material_ids[0];

      return {
        slot_id,
        label_id,
        label_variant_id: variant?.label_variant_id,
        material_id
      };
    }) || [];

  const upsellApiCartItemData = {
    ...primaryApiCartItem,
    product_id: chosenUpsell?.upsell.product_id,
    label_slots: upsellApiCartItemLabelSlots,
    legacy_product_code: getUpsellProductCode(chosenUpsell, properties),
    previewSvg:
      previewSvgOverride ??
      document.getElementById(
        `product-preview-${chosenUpsell?.upsell.product_id}`
      )?.outerHTML ??
      ''
  };

  return upsellApiCartItemData;
}

export function getQuantityProperty(
  slots: Record<string, ItemSlot>,
  apiProduct?: Product | UpsellProduct
) {
  return Object.entries(slots).reduce((acc, [slotId, { labelId }]) => {
    const slot = apiProduct?.label_slots?.find(
      label_slot => label_slot.product_label_slot_id == slotId
    );

    const slotOption = slot?.slot_options?.find(
      slot_option => slot_option.label_id == labelId
    );

    return (acc += slotOption?.quantity ?? 0);
  }, 0);
}

export function addShopifyCartItems(items: object[]) {
  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items, sections: 'right-nav-item' })
  }).then(async cart => {
    try {
      const readableCart = await cart.json();
      const sectionsToUpdate = readableCart.sections;

      Object.keys(sectionsToUpdate).forEach(sectionId => {
        const updateElement = document.getElementById(
          `shopify-section-${sectionId}`
        );
        if (updateElement) {
          updateElement.innerHTML = sectionsToUpdate[sectionId];
        }
      });
    } catch (err) {
      console.warn(`Could not update cart sections: ${err}`);
    } finally {
      // This is fine because return cart has no effect on the try / catch block
      // If this ever does give issues in the future
      // remove finally block and add return cart to catch block
      // eslint-disable-next-line no-unsafe-finally
      return cart;
    }
  });
}

export function updateShopifyCartItem(key: string, primaryProductItem: object) {
  return fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...primaryProductItem, id: key })
  });
}

export function addShopifyCartItem(primaryProductItem: object) {
  return addShopifyCartItems([primaryProductItem]);
}

export function removeShopifyCartItem(key: string) {
  return fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: 0 })
  });
}

export function removeShopifyCartItems(keys: string[]) {
  const updates: Record<string, number> = {};
  keys.forEach(key => (updates[key] = 0));
  return fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates })
  });
}
